import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  link: string;
  created_at: string;
  order: number | null;
}

/**
 * Hook para obtener notificaciones con WebSocket en tiempo real + polling como fallback
 */
export function useNotifications() {
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 5000;

  // Función para emitir eventos de estado del WebSocket
  const emitStatusChange = useCallback((status: 'connecting' | 'connected' | 'disconnected' | 'error') => {
    const event = new CustomEvent('websocket-status-change', {
      detail: { status, timestamp: new Date() }
    });
    window.dispatchEvent(event);
  }, []);

  // Query para obtener notificaciones (con polling lento como fallback)
  const query = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const { data } = await api.get('/api/notifications');
      return data;
    },
    refetchInterval: 60000, // Polling cada 60 segundos (solo como fallback)
    refetchIntervalInBackground: false, // NO actualizar en background
  });

  // Función para manejar nuevas notificaciones
  const handleNewNotification = useCallback((notification: Notification) => {
    console.log('🔔 Nueva notificación recibida:', notification);

    // Mostrar toast
    toast.info(notification.title, {
      description: notification.message,
      duration: 5000,
      action: notification.link ? {
        label: 'Ver',
        onClick: () => window.location.href = notification.link
      } : undefined
    });

    // Reproducir sonido
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {
        // Fallback silencioso
      });
    } catch (error) {
      // Ignorar errores de audio
    }

    // Actualizar cache de React Query
    queryClient.setQueryData<Notification[]>(['notifications'], (old) => {
      if (!old) return [notification];
      return [notification, ...old];
    });

    // Invalidar contador de no leídas
    queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });

  }, [queryClient]);

  // Función para conectar WebSocket
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('tiendaya_token'); // FIX: Usar el nombre correcto del token
    if (!token) {
      console.warn('⚠️ No hay token para WebSocket de notificaciones');
      emitStatusChange('error');
      return;
    }

    try {
      // Cerrar conexión existente
      if (wsRef.current) {
        wsRef.current.close();
      }

      emitStatusChange('connecting');
      console.log('🔌 Intentando conectar WebSocket de notificaciones...');

      // Crear conexión WebSocket (reutilizamos la misma conexión de pedidos)
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // FIX: Usar el host del backend (api.tiendasya.store) en lugar del frontend
      const backendHost = import.meta.env.VITE_API_URL?.replace(/^https?:\/\//, '').replace(/\/$/, '') || 'api.tiendasya.store';
      const wsUrl = `${wsProtocol}//${backendHost}/ws/orders/?token=${token}`;

      console.log('🔌 Conectando WebSocket de notificaciones...');

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // 💓 HEARTBEAT: Enviar ping cada 30 segundos para mantener conexión viva
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
          console.log('💓 Ping enviado para mantener WebSocket vivo');
        }
      }, 30000); // 30 segundos

      // Guardar el interval para limpiarlo después
      ws.pingInterval = pingInterval;

      ws.onopen = () => {
        console.log('✅ ¡WebSocket de notificaciones CONECTADO!');
        console.log('🎉 Las notificaciones ahora son en TIEMPO REAL');
        setIsConnected(true);
        setReconnectAttempts(0);
        emitStatusChange('connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket mensaje recibido:', data.type);

          switch (data.type) {
            case 'connected':
              console.log('🔗 WebSocket confirmado:', data.message);
              emitStatusChange('connected');
              break;

            case 'new_notification':
              // NUEVA FUNCIONALIDAD: Recibir notificaciones en tiempo real
              if (data.notification) {
                console.log('🔔 ¡NUEVA NOTIFICACIÓN VIA WEBSOCKET!');
                console.log('📋 Título:', data.notification.title);
                console.log('💬 Mensaje:', data.notification.message);
                handleNewNotification(data.notification);
              }
              break;

            case 'new_order':
              // También refrescar notificaciones cuando hay nuevo pedido
              console.log('🆕 Nuevo pedido detectado, refrescando notificaciones');
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
              queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
              break;

            case 'heartbeat':
              // Mantener conexión viva
              console.log('💓 Heartbeat recibido');
              break;

            default:
              console.log('📨 Evento WebSocket desconocido:', data.type);
          }
        } catch (error) {
          console.error('❌ Error procesando mensaje WebSocket:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ Error en WebSocket de notificaciones:', error);
        setIsConnected(false);
        emitStatusChange('error');
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket de notificaciones cerrado. Código:', event.code);
        setIsConnected(false);
        emitStatusChange('disconnected');

        // Limpiar el interval de ping
        if (ws.pingInterval) {
          clearInterval(ws.pingInterval);
        }

        // Intentar reconexión
        if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          setReconnectAttempts(prev => prev + 1);
          console.log(`🔄 Reintentando WebSocket en ${RECONNECT_DELAY/1000}s (${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, RECONNECT_DELAY);
        } else {
          console.warn('🚫 Máximo de reintentos alcanzado');
          console.warn('📡 Usando solo polling como fallback');
          emitStatusChange('disconnected');
        }
      };

    } catch (error) {
      console.error('❌ Error creando WebSocket de notificaciones:', error);
      setIsConnected(false);
      emitStatusChange('error');
    }
  }, [handleNewNotification, reconnectAttempts, queryClient, emitStatusChange]);

  // Función de limpieza
  const cleanup = useCallback(() => {
    if (wsRef.current) {
      // Limpiar interval de ping antes de cerrar
      if (wsRef.current.pingInterval) {
        clearInterval(wsRef.current.pingInterval);
      }
      wsRef.current.close();
      wsRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setIsConnected(false);
  }, []);

  // Efecto principal
  useEffect(() => {
    connectWebSocket();
    return cleanup;
  }, [connectWebSocket, cleanup]);

  // Efecto para reconectar cuando la página vuelve a estar visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected) {
        console.log('🔄 Página visible, reconectando WebSocket de notificaciones');
        connectWebSocket();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isConnected, connectWebSocket]);

  return query;
}

/**
 * Hook para obtener el conteo de notificaciones no leídas
 */
export function useUnreadCount() {
  return useQuery<{ count: number }>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const { data } = await api.get('/api/notifications/unread-count');
      return data;
    },
    staleTime: 0, // ⚡ SIEMPRE considerar stale para actualización inmediata
    refetchInterval: 60000, // Polling cada 60 segundos (mismo que notificaciones)
    refetchIntervalInBackground: false, // NO actualizar en background
  });
}

/**
 * Hook para marcar una notificación como leída
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: number) => {
      const { data } = await api.post(`/api/notifications/${notificationId}/read`);
      return data;
    },
    onSuccess: () => {
      // Invalidar queries para refrescar los datos
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });
}

/**
 * Hook para marcar todas las notificaciones como leídas
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/notifications/mark-all-read');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });
}
